import { useState } from "react";
import useTranslations from "../../core/i18n/useTranslations";
import Button from "../../ui-lib/components/Button/Button";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";
import { RowData } from "./ObjectsTable";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import { IItemProp } from "../../ui-lib/components/Dropdown/DropdownItem";
import { notifyApiErrors } from "../../core/helpers/helpers";
import {
  AlarmHandling,
  batchFetchTemporaryHandling,
  batchSaveAlarmHandling,
} from "../../core/api/alarmhandlings";
import useUser from "../../core/user/useUser";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import { Spacer } from "../../components/Layout/Layout";
import Alert from "../../ui-lib/components/Alerts/Alert";

export const AddBatchAlarmHandlingModal = ({
  items,
  onSuccess,
}: {
  items: RowData[];
  onSuccess?: () => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alarmHandlings, setAlarmHandlings] = useState<AlarmHandling[]>([]);
  const [selectedId, setSelectedId] = useState<number>();

  const openModal = async () => {
    setLoading(true);
    const ids = items.map((i) => i.id);
    try {
      const res = await batchFetchTemporaryHandling(authenticatedRequest, ids);
      if (!res.data.length) {
        return notify({
          message: t("Objects:no_available_alam_handlings"),
          variant: "warning",
        });
      }
      setAlarmHandlings(res.data);
      setOpen(true);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors, error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setSelectedId(undefined);
    setAlarmHandlings([]);
  };

  const onSubmit = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const ids = items.map((i) => i.id);
      await batchSaveAlarmHandling(authenticatedRequest, ids, selectedId);
      notify({
        message: t("Objects:alarm_handling_assigned_success"),
      });
      closeModal();
      onSuccess?.();
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors, error.response?.status);
    } finally {
      setSaving(false);
    }
  };

  const dropdownItems: IItemProp[] = alarmHandlings.map((alarmHandling) => ({
    id: alarmHandling.id,
    name: alarmHandling.name,
    isSelected: alarmHandling.id === selectedId,
  }));

  return (
    <>
      <Button
        text={`${t("Objects:change_selection")} (${items.length})`}
        onClick={() => openModal()}
        loading={loading}
      />
      <PrimeModal
        withHeader
        withFooter
        fixedWidth="663px"
        isOpen={open}
        loading={saving}
        header={t("Objects:change_alarm_handling")}
        onClose={closeModal}
        submitBtn={{
          text: `${t("Objects:change_alarm_handling")} (${items.length})`,
          onClick: () => onSubmit(),
          disabled: !selectedId,
        }}
        cancelBtn={{ text: t("Common:Cancel"), onClick: closeModal }}
      >
        <p>{t("Objects:change_alarm_handling_description")}</p>

        <Spacer size={16} />

        <Alert
          withDefaultIcon
          title={t("Objects:temporary_handling_alert_text")}
          variant="warning"
        />
        <Spacer size={16} />

        <Dropdown
          width={376}
          items={dropdownItems}
          selectedItem={dropdownItems.find((item) => item.isSelected)}
          onSelectItem={(item) => setSelectedId(item.id as number)}
          isLoading={loading}
          disabled={loading || !dropdownItems.length}
          title={t("Objects:new_alarm_handling")}
          placeholder={t("Objects:new_alarm_handling_placeholder")}
        />
      </PrimeModal>
    </>
  );
};
